import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  User, 
  Camera, 
  FileText,
  AlertTriangle,
  Heart,
  Plus,
  Edit2,
  Save,
  X,
  Search,
  ChevronRight,
  Users,
  Beaker,
  Crown
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import ScalpCareLog from "./ScalpCareLog";
import HealthIntakeForm from "./HealthIntakeForm";

const ClientNotes = ({ token, membershipTier = "free", isSuperAdmin = false }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notesLoading, setNotesLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeClientTab, setActiveClientTab] = useState("notes");
  const [formData, setFormData] = useState({
    notes: "",
    preferences: "",
    allergies: "",
    photo_url: ""
  });

  // Check if user has premium access (Pro, Elite, OR Super Admin)
  const isPremium = membershipTier === "pro" || membershipTier === "elite" || isSuperAdmin;

  useEffect(() => {
    fetchClients();
  }, [token]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/analytics/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.top_clients || []);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      // If the endpoint fails, try appointments to extract clients
      try {
        const aptResponse = await axios.get(`${API}/appointments/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const clientMap = new Map();
        aptResponse.data.forEach(apt => {
          if (apt.client && !clientMap.has(apt.client.user_id)) {
            clientMap.set(apt.client.user_id, {
              client_id: apt.client.user_id,
              name: apt.client.name,
              visits: 1
            });
          } else if (apt.client) {
            const existing = clientMap.get(apt.client.user_id);
            clientMap.set(apt.client.user_id, {
              ...existing,
              visits: existing.visits + 1
            });
          }
        });
        setClients(Array.from(clientMap.values()));
      } catch (e) {
        console.error("Failed to fetch appointments:", e);
      }
    } finally {
      setLoading(false);
    }
  };

  const selectClient = async (client) => {
    setSelectedClient(client);
    setNotesLoading(true);
    setIsEditing(false);
    
    try {
      const response = await axios.get(`${API}/client-notes/client/${client.client_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(response.data);
      if (response.data.length > 0) {
        const latest = response.data[0];
        setFormData({
          notes: latest.notes || "",
          preferences: latest.preferences || "",
          allergies: latest.allergies || "",
          photo_url: latest.photo_url || ""
        });
      } else {
        setFormData({
          notes: "",
          preferences: "",
          allergies: "",
          photo_url: ""
        });
      }
    } catch (error) {
      console.error("Failed to fetch notes:", error);
      setNotes([]);
      setFormData({
        notes: "",
        preferences: "",
        allergies: "",
        photo_url: ""
      });
    } finally {
      setNotesLoading(false);
    }
  };

  const saveNote = async () => {
    if (!selectedClient) return;
    
    try {
      await axios.post(`${API}/client-notes/`, {
        client_id: selectedClient.client_id,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client notes saved!");
      setIsEditing(false);
      selectClient(selectedClient); // Refresh notes
    } catch (error) {
      toast.error("Failed to save notes");
    }
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Client Notes</h2>
        <p className="text-gray-500">Track preferences, allergies, and service history for each client</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Client List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              Your Clients
            </CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto">
            {filteredClients.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No clients found</p>
                <p className="text-gray-400 text-xs mt-1">
                  Clients will appear here after their first appointment
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredClients.map((client) => (
                  <button
                    key={client.client_id}
                    onClick={() => selectClient(client)}
                    className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                      selectedClient?.client_id === client.client_id
                        ? "bg-gradient-to-r from-orange-100 to-amber-100 border-2 border-orange-300"
                        : "bg-gray-50 hover:bg-gray-100 border-2 border-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        selectedClient?.client_id === client.client_id
                          ? "bg-gradient-to-br from-orange-500 to-amber-500"
                          : "bg-gray-200"
                      }`}>
                        <User className={`w-5 h-5 ${
                          selectedClient?.client_id === client.client_id
                            ? "text-white"
                            : "text-gray-500"
                        }`} />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-800">{client.name}</p>
                        <p className="text-xs text-gray-500">{client.visits} visit{client.visits !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${
                      selectedClient?.client_id === client.client_id
                        ? "text-orange-500"
                        : "text-gray-400"
                    }`} />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes Panel */}
        <div className="lg:col-span-2">
          {!selectedClient ? (
            <Card>
              <CardContent className="py-16 text-center">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">Select a Client</h3>
                <p className="text-gray-400 text-sm">
                  Choose a client from the list to view or edit their notes
                </p>
              </CardContent>
            </Card>
          ) : notesLoading ? (
            <Card>
              <CardContent className="py-16 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                <p className="text-gray-500 mt-4">Loading notes...</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-800">{selectedClient.name}</h3>
                    <p className="text-sm text-gray-500">{selectedClient.visits} visit{selectedClient.visits !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                {activeClientTab === "notes" && (
                  <div className="flex gap-2">
                    {!isEditing ? (
                      <Button 
                        size="sm"
                        className="bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                        onClick={() => setIsEditing(true)}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit Notes
                      </Button>
                    ) : (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setIsEditing(false);
                            if (notes.length > 0) {
                              const latest = notes[0];
                              setFormData({
                                notes: latest.notes || "",
                                preferences: latest.preferences || "",
                                allergies: latest.allergies || "",
                                photo_url: latest.photo_url || ""
                              });
                            }
                          }}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                        <Button 
                          size="sm"
                          className="bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                          onClick={saveNote}
                        >
                          <Save className="w-4 h-4 mr-1" />
                          Save
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Client Tabs - Notes, Scalp Care, Health Profile */}
              <Tabs value={activeClientTab} onValueChange={setActiveClientTab}>
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="notes" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">Notes</span>
                  </TabsTrigger>
                  <TabsTrigger value="scalp" className="flex items-center gap-2">
                    <Beaker className="w-4 h-4" />
                    <span className="hidden sm:inline">Scalp Care</span>
                    {isPremium && <Badge variant="secondary" className="text-xs px-1 py-0 ml-1 hidden md:inline">Pro</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="health" className="flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    <span className="hidden sm:inline">Health</span>
                    {isPremium && <Badge variant="secondary" className="text-xs px-1 py-0 ml-1 hidden md:inline">Pro</Badge>}
                  </TabsTrigger>
                </TabsList>

                {/* Notes Tab Content */}
                <TabsContent value="notes" className="space-y-4">
                  {/* Photo Section */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          {formData.photo_url ? (
                            <img 
                              src={formData.photo_url} 
                          alt="Client style reference"
                          className="w-20 h-20 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center">
                          <Camera className="w-8 h-8 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm text-gray-600">Style Reference Photo URL</Label>
                      {isEditing ? (
                        <Input
                          value={formData.photo_url}
                          onChange={(e) => setFormData({...formData, photo_url: e.target.value})}
                          placeholder="https://example.com/photo.jpg"
                          className="mt-1"
                        />
                      ) : (
                        <p className="text-sm text-gray-500 mt-1">
                          {formData.photo_url ? "Reference photo saved" : "No reference photo added"}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Preferences */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Heart className="w-4 h-4 text-pink-500" />
                    Preferences
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    <Textarea
                      value={formData.preferences}
                      onChange={(e) => setFormData({...formData, preferences: e.target.value})}
                      placeholder="e.g., Prefers 2 inches off the sides, likes quiet conversation, always wants product recommendations..."
                      rows={3}
                    />
                  ) : (
                    <p className="text-gray-600">
                      {formData.preferences || "No preferences recorded yet"}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Allergies */}
              <Card className="border-red-200 bg-red-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-4 h-4" />
                    Allergies & Sensitivities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    <Textarea
                      value={formData.allergies}
                      onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                      placeholder="e.g., Allergic to certain hair dyes, sensitive scalp, latex allergy..."
                      rows={2}
                      className="border-red-200"
                    />
                  ) : (
                    <p className={formData.allergies ? "text-red-700 font-medium" : "text-gray-500"}>
                      {formData.allergies || "No allergies recorded"}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* General Notes */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-500" />
                    Service Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Notes about previous services, styling tips, product preferences, conversation topics..."
                      rows={4}
                    />
                  ) : (
                    <p className="text-gray-600 whitespace-pre-wrap">
                      {formData.notes || "No notes recorded yet"}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Notes History */}
              {notes.length > 1 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Previous Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {notes.slice(1).map((note, index) => (
                        <div key={note.note_id || index} className="p-3 bg-gray-50 rounded-lg text-sm">
                          <p className="text-gray-600">{note.notes}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(note.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
                </TabsContent>

                {/* Scalp Care Log Tab */}
                <TabsContent value="scalp">
                  <ScalpCareLog 
                    clientId={selectedClient.client_id} 
                    clientName={selectedClient.name}
                    isPremium={isPremium}
                  />
                </TabsContent>

                {/* Health Profile Tab */}
                <TabsContent value="health">
                  <HealthIntakeForm 
                    clientId={selectedClient.client_id} 
                    clientName={selectedClient.name}
                    isPremium={isPremium}
                    onUpdate={() => selectClient(selectedClient)}
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientNotes;
